import { Box, Grid } from '@mui/material';
import { useState, useEffect, memo } from 'react';
import { AutocompleteDropdown } from 'src/shared/components/form/autocompleteDropdown';
import { getAssociations } from 'src/shared/services/associationService';
import { getDistricts } from 'src/shared/services/districtService';
import { Association } from 'src/shared/models/association/association';
import { District } from 'src/shared/models/district/district';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getSysProducts } from 'src/shared/functions/getSysProducts';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { SysProductYvNameEnum } from 'src/shared/models/enums/sysProductEnums';
import { useTranslation } from 'react-i18next';
import { ControlledAutocompleteDropdown } from '../form/controlledAutocompleteDropdown';

type AssociationAndDistrictSelectProps = {
  hideDistrict: boolean;
  district: District | undefined;
  districtCallback: (val: District | undefined) => void;
  districtFormFieldName?: string;
  association: Association | undefined;
  associationCallback: (val: Association | undefined) => void;
  associationFormFieldName?: string;
  useControlled?: boolean;
  useGridLayout?: boolean;
  useLazyLoading?: boolean;
  showPlaceholder?: boolean;
  gridXs?: number;
  gridLg?: number;
  initialDistrictId?: number;
  initialAssociationId?: number;
  disabled?: boolean;
  disableDistrict?: boolean;
  disableAssociation?: boolean;
  districtMb?: number | string;
};

export const AssociationAndDistrictSelect = (props: AssociationAndDistrictSelectProps) => {
  const {
    hideDistrict,
    district,
    districtCallback,
    districtFormFieldName = 'countyAss_id',
    districtMb,
    association,
    associationCallback,
    associationFormFieldName = 'localAss_id',
    useGridLayout = false,
    useControlled = false,
    useLazyLoading = false,
    showPlaceholder = false,
    gridLg = false,
    gridXs = false,
    initialDistrictId,
    initialAssociationId,
    disabled = false,
    disableDistrict = false,
    disableAssociation = false,
  } = props;
  const [associations, setAssociations] = useState<Association[]>();
  const [localAssociation, setLocalAssociation] = useState<Association>();
  const [districts, setDistricts] = useState<District[]>();
  const [localDistrict, setLocalDistrict] = useState<District>();

  const { currentUser } = useGlobals();
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  // /Association- och /District-endpointerna kräver memberReg-modulen och returnerar 403 annars
  // (axios-interceptorn visar då en röd toast). Hoppa båda fetcherna om användaren saknar modulen.
  const hasMemberReg = getSysProducts(currentUser).some((x) => x.yvName.toLowerCase() === SysProductYvNameEnum.MemberReg.toLowerCase());

  useEffect(() => {
    if (!hasMemberReg) return;
    const fetchDistricts = async () => {
      try {
        const result = await getDistricts(0, 10000, 'countyAss', 'asc', true);
        if (isMounted()) {
          const items = result.data.results.sort((a, b) => (a.countyAss > b.countyAss ? 1 : -1));
          setDistricts(items);
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.log(e);
        }
      }
    };

    if (!hideDistrict) {
      fetchDistricts();
    }
  }, [isMounted, hideDistrict, hasMemberReg]);

  useEffect(() => {
    if (!hasMemberReg) return;
    const fetchData = async () => {
      try {
        const result = await getAssociations(district?.countyAss_id);
        if (isMounted()) {
          const items = result.data.results.sort((a, b) => (a.localAss > b.localAss ? 1 : -1));
          setAssociations(items);
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.log(e);
        }
      }
    };

    fetchData();
  }, [isMounted, district, hasMemberReg]);

  const updateAssociation = (val: Association | undefined) => {
    // ConsoleHelper.log('updateAssociation', val);
    associationCallback(val);
    setLocalAssociation(val);
  };

  const updateDistrict = (val: District | undefined) => {
    // ConsoleHelper.log('updateDistrict', val);
    districtCallback(val);
    setLocalDistrict(val);
    associationCallback(undefined);
    setLocalAssociation(undefined);
  };

  useEffect(() => {
    setLocalAssociation(association);
  }, [association]);

  useEffect(() => {
    setLocalDistrict(district);
  }, [district]);

  useEffect(() => {
    if (initialDistrictId && districts && districts.length > 0) {
      const matchedDistrict = districts.find((d) => d.countyAss_id === initialDistrictId);
      if (matchedDistrict) {
        setLocalDistrict(matchedDistrict);
      }
    }
  }, [districts, initialDistrictId]);

  useEffect(() => {
    if (initialAssociationId && associations && associations.length > 0) {
      const matchedAssociation = associations.find((d) => d.localAss_id === initialAssociationId);
      if (matchedAssociation) {
        setLocalAssociation(matchedAssociation);
      }
    }
  }, [associations, initialAssociationId]);

  const Layout = ({ children }) => {
    return useGridLayout ? (
      <Grid item xs={gridXs || false} lg={gridLg}>
        {children}
      </Grid>
    ) : (
      <Box>{children}</Box>
    );
  };

  return (
    <>
      {districts && districts.length > 0 && !hideDistrict && (
        <Layout>
          <Box
            sx={{
              ...(useGridLayout ? null : { mr: 2 }),
              ...(districtMb !== undefined ? { mb: districtMb } : null),
            }}
          >
            {useControlled ? (
              <ControlledAutocompleteDropdown<District>
                disable={disabled || disableDistrict}
                options={districts}
                nameProp='countyAss'
                name={districtFormFieldName || 'countyAss_id'}
                label={getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.CountyLabel, t('member:tableHeaders.district'))}
                {...(useGridLayout ? { componentWidth: '100%' } : null)}
                callback={(val) => updateDistrict(val || undefined)}
                {...(showPlaceholder ? { placeholder: t('common:form.all') } : null)}
              />
            ) : (
              <AutocompleteDropdown<District>
                disable={disabled || disableDistrict}
                options={districts}
                nameProp='countyAss'
                keyProp='countyAss_id'
                name={districtFormFieldName || 'countyAss_id'}
                label={getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.CountyLabel, t('member:tableHeaders.district'))}
                value={localDistrict}
                {...(useGridLayout ? { componentWidth: '100%' } : null)}
                callback={(val) => updateDistrict(val || undefined)}
                {...(showPlaceholder ? { placeholder: t('common:form.all') } : null)}
              />
            )}
          </Box>
        </Layout>
      )}
      {associations && associations.length > 0 && (
        <Layout>
          {useControlled ? (
            <ControlledAutocompleteDropdown<Association>
              disable={disabled || disableAssociation}
              options={associations}
              nameProp='localAss'
              name={associationFormFieldName || 'localAss_id'}
              label={getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.LocalLabel, t('member:tableHeaders.association'))}
              {...(useGridLayout ? { componentWidth: '100%' } : null)}
              callback={(val) => updateAssociation(val || undefined)}
              {...(showPlaceholder ? { placeholder: t('common:form.all') } : null)}
            />
          ) : (
            <AutocompleteDropdown<Association>
              disable={disabled || disableAssociation}
              options={associations}
              nameProp='localAss'
              keyProp='localAss_id'
              name={associationFormFieldName || 'localAss_id'}
              label={getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.LocalLabel, t('member:tableHeaders.association'))}
              value={localAssociation}
              {...(useGridLayout ? { componentWidth: '100%' } : null)}
              callback={(val) => updateAssociation(val || undefined)}
              {...(showPlaceholder ? { placeholder: t('common:form.all') } : null)}
            />
          )}
        </Layout>
      )}
    </>
  );
};

export const MemoizedAssociationAndDistrictSelect = memo(AssociationAndDistrictSelect);
